import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { TranslateService } from '@ngx-translate/core';
import unset from 'lodash-es/unset';
import { Observable, defer, of } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

import { ArticleRepositoryPort } from 'article/port/article-repository.port';
import { ModuleColumn } from 'module/column/module-column';
import { ModuleColumnI18n } from 'module/column/module-column-i18n';
import { ModuleDependencyType } from 'module/model/module-dependency.type';
import { ModuleType } from 'module/model/module.type';
import { Module } from 'module/module';
import { ModuleDependency } from 'module/module-dependency';
import { ModuleI18n } from 'module/module-i18n';
import { ModuleOption } from 'module/module-option';
import { ModulePageRepositoryPort } from 'module/page/port/module-page-repository.port';
import { PhaseRepositoryPort } from 'phase/port/phase-repository.port';

import { AppLocator } from '../../app.locator';

export interface ModuleDependencyDocument {
	readonly dependencyId: string;
	readonly moduleId: string;
	readonly type: string;
}

export interface ModuleOptionDocument {
	readonly label: string;
	readonly value: string;
	readonly groupName: string;
	readonly position: number;
}

export interface ModuleI18nDocument {
	readonly name: string;
	readonly title: string;
	readonly locale: string;
	readonly videoId: string;
	readonly description: string;
	readonly suggestionText: string;
	readonly averageDuration: string;
	readonly options: ReadonlyArray<ModuleOptionDocument>;
}

export interface ModuleColumnI18nDocument {
	readonly name: string;
	readonly locale: string;
}

export interface ModuleColumnDocument {
	readonly id: string;
	readonly position: number;
	readonly localizations: ReadonlyArray<ModuleColumnI18nDocument>;
}

export interface ModuleDocument {
	readonly id: string;
	readonly type: string;
	readonly phaseId: string;
	readonly moduleId: string;
	readonly position: number;
	readonly sectionId: string;
	readonly pagesId: string[];
	readonly redirectUrl: string;
	readonly pushModuleId: string;
	readonly hasSettings: boolean;
	readonly isVisibleInPlan: boolean;
	readonly canTriggerAnswersDocument: boolean;
	readonly averageSpentTime: string;
	readonly averageSpentDays: number;
	readonly articlesId: string[];
	readonly organizationId: string;
	readonly updateTimestamp: number;
	readonly linkedGraphsId: string[];
	readonly linkedQuestionsId: string[];
	readonly excludedQuestionsId: string[];
	readonly columns: ModuleColumnDocument[];
	readonly localizations: ModuleI18nDocument[];
	readonly dependencies: ModuleDependencyDocument[];
}

export type ModuleState = EntityState<ModuleDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'modules' })
export class ModuleStore extends EntityStore<ModuleState> {
	constructor() {
		super();
	}

	public get _translateService(): TranslateService {
		return AppLocator.inject(TranslateService);
	}

	public get _modulePageRepositoryPort(): ModulePageRepositoryPort {
		return AppLocator.inject<ModulePageRepositoryPort>(ModulePageRepositoryPort);
	}

	public get _phaseRepositoryPort(): PhaseRepositoryPort {
		return AppLocator.inject<PhaseRepositoryPort>(PhaseRepositoryPort);
	}

	public get _articleRepositoryPort(): ArticleRepositoryPort {
		return AppLocator.inject<ArticleRepositoryPort>(ArticleRepositoryPort);
	}

	public asDocument(module: Module): ModuleDocument {
		const document: ModuleDocument = module
			? {
					id: module.id,
					type: module.type,
					canTriggerAnswersDocument: module.canTriggerAnswersDocument,
					averageSpentTime: module.averageSpentTime,
					averageSpentDays: module.averageSpentDays,
					phaseId: module.phaseId,
					moduleId: module.moduleId,
					pagesId: module.pagesId,
					position: module.position,
					sectionId: module.sectionId,
					articlesId: module.articlesId,
					hasSettings: module.hasSettings,
					redirectUrl: module.redirectUrl,
					pushModuleId: module.pushModuleId,
					organizationId: module.organizationId,
					linkedGraphsId: module.linkedGraphsId,
					isVisibleInPlan: module.isVisibleInPlan,
					updateTimestamp: module.updateTimestamp,
					linkedQuestionsId: module.linkedQuestionsId,
					excludedQuestionsId: module.excludedQuestionsId,
					dependencies: module.dependencies.map((dependency) => ({
						moduleId: dependency.moduleId,
						dependencyId: dependency.dependencyId,
						type: dependency.type,
					})),
					columns: module.columns.map((column) => ({
						id: column.id,
						position: column.position,
						localizations: column.localizations.map((loc) => ({
							locale: loc.locale,
							name: loc.name,
						})),
					})),
					localizations: module.localizations.map((localization) => ({
						name: localization.name,
						title: localization.title,
						locale: localization.locale,
						videoId: localization.videoId,
						description: localization.description,
						suggestionText: localization.suggestionText,
						averageDuration: localization.averageDuration,
						options: localization.options.map((option) => ({
							value: option.value,
							label: option.label,
							groupName: option.groupName,
							position: option.position,
						})),
					})),
			  }
			: null;

		if ((module.linkedQuestionsId || []).length === 0) {
			unset(document, 'linkedQuestionsId');
		}

		if ((module.linkedGraphsId || []).length === 0) {
			unset(document, 'linkedGraphsId');
		}

		if (!module.pushModuleId) {
			unset(document, 'pushModuleId');
		}

		if ((module.articlesId || []).length === 0) {
			unset(document, 'articlesId');
		}

		if ((module.dependencies || []).length === 0) {
			unset(document, 'dependencies');
		}

		if ((module.columns || []).length === 0) {
			unset(document, 'columns');
		}

		if ((module.pagesId || []).length === 0) {
			unset(document, 'pagesId');
		}

		return document;
	}

	public asEntity(document: ModuleDocument): Module {
		const pages$ = document?.pagesId
			? this._modulePageRepositoryPort.observePagesByIds(document.pagesId)
			: of([]);

		const articles$ = document?.articlesId
			? this._articleRepositoryPort.observeArticlesByIds(document.articlesId)
			: of([]);

		const localizations = !!document
			? document.localizations.map(
					(loc) =>
						new ModuleI18n(
							loc.name,
							loc.title,
							loc.locale,
							loc.videoId,
							loc.description,
							loc.suggestionText,
							loc.averageDuration,
							loc.options.map(
								(option) =>
									new ModuleOption(
										option.label,
										option.value,
										option.groupName,
										option.position
									)
							)
						)
			  )
			: [];

		return document
			? new Module(
					document.id,
					document.canTriggerAnswersDocument,
					document.averageSpentTime,
					document.averageSpentDays,
					document.phaseId,
					document.moduleId,
					ModuleType[document.type],
					document.position,
					document.pagesId,
					document.sectionId,
					document.redirectUrl,
					document.articlesId,
					document.hasSettings,
					document.pushModuleId,
					document.organizationId,
					(document.columns || []).map((column) => {
						const columnLocalizations = column.localizations.map(
							(loc) => new ModuleColumnI18n(loc.name, loc.locale)
						);
						return new ModuleColumn(
							column.id,
							column.position,
							columnLocalizations,
							this._observeLocalization(columnLocalizations)
						);
					}),
					document.updateTimestamp,
					document.isVisibleInPlan,
					document.linkedGraphsId,
					localizations,
					document.linkedQuestionsId,
					document.excludedQuestionsId,
					(document.dependencies || []).map(
						(dependency) =>
							new ModuleDependency(
								dependency.moduleId,
								dependency.dependencyId,
								ModuleDependencyType[dependency.type]
							)
					),
					this._phaseRepositoryPort.observePhaseById(document.phaseId),
					pages$,
					articles$,
					this._observeLocalization(localizations)
			  )
			: null;
	}

	private _observeLocalization<T extends { locale: string }>(
		localizations: ReadonlyArray<T>
	): Observable<T> {
		return localizations && localizations.length > 0
			? defer(() =>
					this._translateService.onLangChange.pipe(
						map((e) => e.lang),
						startWith(this._translateService.currentLang)
					)
			  ).pipe(
					map((lang) => localizations.find((loc) => loc.locale === lang)),
					map((loc) => (!loc ? localizations[0] : loc)),
					shareReplay({ bufferSize: 1, refCount: true })
			  )
			: of(null);
	}
}
